import LangEnum from '../../../lang/LangEnum';
import PATHS from '../../../routes/routes.const';
import { replacePathId } from '../../../routes/utils/replacePathId';
import type {
  ActivityFeedActorDTO,
  ActivityFeedOrderTotalsDTO,
  ActivityFeedRefsDTO,
  ActivityFeedRowDTO,
  ActivityFeedSource,
} from '../../../services/modules/activityFeed/activityFeed.types';
import { TargetStatus, type TargetUnit } from '../../../services/modules/target/target.types';
import { formatDate } from '../../../utils/dates';
import type { InviteStoreStatus } from '../../my-network/myNetwork.types';
import { TARGET_DATE_FORMAT } from '../../target/target.constants';
import { TargetPaths } from '../../target/target.paths';
import type {
  ActivityFeedAttainment,
  ActivityFeedChildOrder,
  ActivityFeedOrderTotals,
  ActivityFeedPhrase,
  ActivityFeedPhraseDetail,
  ActivityFeedPhraseKeyByStatus,
  ActivityFeedRowOf,
  ActivityFeedSelfRefs,
  ActivityFeedStatusChip,
  ActivityFeedStatusPair,
  ChildOrdersParams,
  ConnectionPhraseParams,
  OrderCreatedPhraseKeyParams,
  OrderPhraseKeyParams,
  TargetLossReasonParams,
  TargetPeriodRange,
  TargetPhraseParams,
  TargetStatusAttainmentParams,
} from './activityFeedPhrase.types';
import {
  CONNECTION_CHIP_BY_BRAND_STATUS,
  CUSTOMER_CHIP_BY_STATUS,
  INTEGRATION_CHIP_BY_STATUS,
  ORDER_CHIP_BY_STATUS,
  TARGET_CHIP_BY_STATUS,
  findActivityFeedStatusChip,
} from './activityFeedStatusChip.constants';
import {
  TARGET_LOSS_REASON_LABEL,
  TARGET_VALUE_DETAIL_BY_STATUS,
  TargetValueDetail,
  isTargetStatus,
} from './activityFeedTarget.constants';

const ORIGIN_DETAIL_BY_SOURCE: Partial<Record<ActivityFeedSource, LangEnum>> = {
  integration: LangEnum.ACTIVITY_FEED_DETAIL_ORIGIN_ERP,
  spreadsheet: LangEnum.ACTIVITY_FEED_DETAIL_ORIGIN_SPREADSHEET,
  rachel: LangEnum.ACTIVITY_FEED_DETAIL_ORIGIN_RACHEL,
  automation: LangEnum.GLOBAL_AUTOMATIC,
};

const CONNECTION_PHRASE_BY_CREATED_BRAND_STATUS: ActivityFeedPhraseKeyByStatus = {
  SUGERIDO_VENDEDOR: LangEnum.ACTIVITY_FEED_CONNECTION_SUGGESTED,
};

const CONNECTION_STATUSLESS_PHRASE_BY_BRAND_STATUS_PAIR: ActivityFeedPhraseKeyByStatus = {
  'SUGERIDO_VENDEDOR>RECUSADO_ADMIN': LangEnum.ACTIVITY_FEED_CONNECTION_SUGGESTION_REFUSED,
};

const CONNECTION_STATUSLESS_PHRASE_BY_NEW_BRAND_STATUS: ActivityFeedPhraseKeyByStatus = {
  RECUSADO_ADMIN: LangEnum.ACTIVITY_FEED_CONNECTION_REFUSED,
};

const CONNECTION_STATUSLESS_PHRASE_BY_NEW_STORE_STATUS: Record<InviteStoreStatus, LangEnum | null> =
  {
    SOLICITADO: null,
    AGUARDANDO: null,
    CONECTADO: null,
    IGNORADO: LangEnum.ACTIVITY_FEED_CONNECTION_INVITE_IGNORED,
  };

const ACTOR_LABEL_BY_SOURCE: Partial<Record<ActivityFeedSource, LangEnum>> = {
  web: LangEnum.GLOBAL_SYSTEM,
  integration: LangEnum.GLOBAL_INTEGRATION,
  spreadsheet: LangEnum.GLOBAL_SPREADSHEET,
  system: LangEnum.GLOBAL_SYSTEM,
  automation: LangEnum.GLOBAL_SYSTEM,
  rachel: LangEnum.GLOBAL_RACHEL,
};

const CUSTOMER_CREATED_ACTOR_LABEL_BY_SOURCE: Record<ActivityFeedSource, LangEnum | null> = {
  web: LangEnum.GLOBAL_UNIDENTIFIED_USER,
  integration: LangEnum.GLOBAL_INTEGRATION,
  spreadsheet: LangEnum.GLOBAL_UNIDENTIFIED_USER,
  system: null,
  automation: LangEnum.GLOBAL_UNIDENTIFIED_USER,
  rachel: LangEnum.GLOBAL_UNIDENTIFIED_USER,
};

export const formatActivityFeedActor = (actor: ActivityFeedActorDTO) => {
  if (!actor.company) {
    return actor.name;
  }

  return `${actor.name} (${actor.company})`;
};

const getActorValues = (actor: ActivityFeedActorDTO | null): Record<string, string> => {
  if (!actor) {
    return {};
  }

  return { actor: formatActivityFeedActor(actor) };
};

const getActorLabel = (actor: ActivityFeedActorDTO | null, source: ActivityFeedSource) => {
  if (actor) {
    return null;
  }

  return ACTOR_LABEL_BY_SOURCE[source] ?? LangEnum.GLOBAL_SYSTEM;
};

const getStatusPairKey = ({ oldStatus, newStatus }: ActivityFeedStatusPair) =>
  `${oldStatus.toUpperCase()}>${newStatus.toUpperCase()}`;

const getOrderRefHref = ({ orderId }: ActivityFeedRefsDTO, selfOrderId?: string) => {
  if (!orderId || orderId === selfOrderId) {
    return null;
  }

  return replacePathId(PATHS.main.orders.editForm.redirect, orderId);
};

const isSelfOrderRow = ({ orderId }: ActivityFeedRefsDTO, selfOrderId?: string) =>
  Boolean(orderId) && orderId === selfOrderId;

const resolveOrderPhraseKey = ({ key, selfKey, refs, selfOrderId }: OrderPhraseKeyParams) =>
  isSelfOrderRow(refs, selfOrderId) ? selfKey : key;

const getTargetRefHref = ({ targetId }: ActivityFeedRefsDTO, selfTargetId?: string) => {
  if (!targetId || targetId === selfTargetId) {
    return null;
  }

  return TargetPaths.detail(targetId);
};

const isSelfTargetRow = ({ targetId }: ActivityFeedRefsDTO, selfTargetId?: string) =>
  Boolean(targetId) && targetId === selfTargetId;

const resolveTargetPhrase = ({
  ownFeedKey,
  withCodeKey,
  targetCode,
  refs,
  selfTargetId,
  values = {},
}: TargetPhraseParams): Pick<ActivityFeedPhrase, 'key' | 'values'> => {
  if (isSelfTargetRow(refs, selfTargetId)) {
    return { key: ownFeedKey, values };
  }

  return { key: withCodeKey, values: { ...values, target: targetCode } };
};

const getOriginDetails = (
  actor: ActivityFeedActorDTO | null,
  source: ActivityFeedSource,
): ActivityFeedPhraseDetail[] => {
  if (!actor) {
    return [];
  }

  const key = ORIGIN_DETAIL_BY_SOURCE[source];

  return key ? [{ key }] : [];
};

const getReasonDetails = (reason: string | null): ActivityFeedPhraseDetail[] => {
  if (!reason) {
    return [];
  }

  return [{ key: LangEnum.ACTIVITY_FEED_DETAIL_REASON, values: { reason } }];
};

const getCustomerCreatedPhraseKey = (status: ActivityFeedStatusChip | null, isPassive: boolean) => {
  if (isPassive) {
    return status
      ? LangEnum.ACTIVITY_FEED_CUSTOMER_CREATED_PASSIVE
      : LangEnum.ACTIVITY_FEED_CUSTOMER_CREATED_PASSIVE_WITHOUT_STATUS;
  }

  return status
    ? LangEnum.ACTIVITY_FEED_CUSTOMER_CREATED
    : LangEnum.ACTIVITY_FEED_CUSTOMER_CREATED_WITHOUT_STATUS;
};

const getCustomerCreatedOriginDetails = (
  actor: ActivityFeedActorDTO | null,
  source: ActivityFeedSource,
): ActivityFeedPhraseDetail[] => {
  const key = ORIGIN_DETAIL_BY_SOURCE[source];

  if (!key || (!actor && source === 'integration')) {
    return [];
  }

  return [{ key }];
};

const resolveCustomerCreatedPhrase = (
  row: ActivityFeedRowOf<'customer.created'>,
): ActivityFeedPhrase | null => {
  const payloadStatus = row.payload.status;
  const status = payloadStatus
    ? findActivityFeedStatusChip(CUSTOMER_CHIP_BY_STATUS, payloadStatus)
    : null;

  if (payloadStatus && !status) {
    return null;
  }

  const isPassive = !row.actor && row.source === 'system';

  return {
    key: getCustomerCreatedPhraseKey(status, isPassive),
    values: getActorValues(row.actor),
    actorLabel:
      row.actor || isPassive
        ? null
        : (CUSTOMER_CREATED_ACTOR_LABEL_BY_SOURCE[row.source] ?? LangEnum.GLOBAL_SYSTEM),
    status,
    details: getCustomerCreatedOriginDetails(row.actor, row.source),
    totals: null,
    attainment: null,
    lossReason: null,
    childOrders: null,
    refHref: null,
  };
};

const resolveCustomerStatusPhrase = (
  row: ActivityFeedRowOf<'customer.status.updated'>,
): ActivityFeedPhrase | null => {
  const status = findActivityFeedStatusChip(CUSTOMER_CHIP_BY_STATUS, row.payload.newStatus);

  if (!status) {
    return null;
  }

  return {
    key: LangEnum.ACTIVITY_FEED_CUSTOMER_STATUS_MOVED,
    values: getActorValues(row.actor),
    actorLabel: getActorLabel(row.actor, row.source),
    status,
    details: [...getReasonDetails(row.payload.reason), ...getOriginDetails(row.actor, row.source)],
    totals: null,
    attainment: null,
    lossReason: null,
    childOrders: null,
    refHref: null,
  };
};

const resolveCustomerIntegrationStatusPhrase = (
  row: ActivityFeedRowOf<'customer.integration_status.updated'>,
): ActivityFeedPhrase | null => {
  const { integrationStatus, message } = row.payload;
  const status = findActivityFeedStatusChip(INTEGRATION_CHIP_BY_STATUS, integrationStatus);

  if (!status) {
    return null;
  }

  return {
    key: LangEnum.ACTIVITY_FEED_CUSTOMER_INTEGRATION_STATUS_MOVED,
    values: {},
    actorLabel: null,
    status,
    details: message ? [{ text: message }] : [],
    totals: null,
    attainment: null,
    lossReason: null,
    childOrders: null,
    refHref: null,
  };
};

const resolveConnectionChipPhrase = ({
  row,
  brandStatus,
  key,
}: ConnectionPhraseParams): ActivityFeedPhrase | null => {
  const status = findActivityFeedStatusChip(CONNECTION_CHIP_BY_BRAND_STATUS, brandStatus);

  if (!status) {
    return null;
  }

  return {
    key,
    values: getActorValues(row.actor),
    actorLabel: getActorLabel(row.actor, row.source),
    status,
    details: [],
    totals: null,
    attainment: null,
    lossReason: null,
    childOrders: null,
    refHref: null,
  };
};

const resolveConnectionStatuslessPhrase = (
  row: ActivityFeedRowDTO,
  key: LangEnum,
): ActivityFeedPhrase => ({
  key,
  values: getActorValues(row.actor),
  actorLabel: getActorLabel(row.actor, row.source),
  status: null,
  details: [],
  totals: null,
  attainment: null,
  lossReason: null,
  childOrders: null,
  refHref: null,
});

const resolveConnectionCreatedPhrase = (
  row: ActivityFeedRowOf<'invite.created'>,
): ActivityFeedPhrase | null => {
  const { brandStatus } = row.payload;
  const statuslessKey = CONNECTION_PHRASE_BY_CREATED_BRAND_STATUS[brandStatus.toUpperCase()];

  if (statuslessKey) {
    return resolveConnectionStatuslessPhrase(row, statuslessKey);
  }

  return resolveConnectionChipPhrase({
    row,
    brandStatus,
    key: LangEnum.ACTIVITY_FEED_CONNECTION_CREATED,
  });
};

const resolveConnectionStoreStatusPhrase = (
  row: ActivityFeedRowOf<'invite.status.updated'>,
): ActivityFeedPhrase | null => {
  const { oldStoreStatus, newStoreStatus } = row.payload;

  if (!newStoreStatus || oldStoreStatus?.toUpperCase() === newStoreStatus.toUpperCase()) {
    return null;
  }

  const phraseKeys: Partial<Record<string, LangEnum | null>> =
    CONNECTION_STATUSLESS_PHRASE_BY_NEW_STORE_STATUS;
  const statuslessKey = phraseKeys[newStoreStatus.toUpperCase()];

  if (!statuslessKey) {
    return null;
  }

  return resolveConnectionStatuslessPhrase(row, statuslessKey);
};

const resolveConnectionStatusPhrase = (
  row: ActivityFeedRowOf<'invite.status.updated'>,
): ActivityFeedPhrase | null => {
  const { oldBrandStatus, newBrandStatus } = row.payload;

  if (oldBrandStatus.toUpperCase() === newBrandStatus.toUpperCase()) {
    return resolveConnectionStoreStatusPhrase(row);
  }

  const pairKey = getStatusPairKey({
    oldStatus: oldBrandStatus,
    newStatus: newBrandStatus,
  });
  const statuslessKey =
    CONNECTION_STATUSLESS_PHRASE_BY_BRAND_STATUS_PAIR[pairKey] ??
    CONNECTION_STATUSLESS_PHRASE_BY_NEW_BRAND_STATUS[newBrandStatus.toUpperCase()];

  if (statuslessKey) {
    return resolveConnectionStatuslessPhrase(row, statuslessKey);
  }

  return resolveConnectionChipPhrase({
    row,
    brandStatus: newBrandStatus,
    key: LangEnum.ACTIVITY_FEED_CONNECTION_MOVED,
  });
};

const resolveConnectionRemovedPhrase = (
  row: ActivityFeedRowOf<'invite.removed'>,
): ActivityFeedPhrase =>
  resolveConnectionStatuslessPhrase(row, LangEnum.ACTIVITY_FEED_CONNECTION_REMOVED);

const getOrderTotals = ({
  totalValue,
  totalItems,
  totalPieces,
}: ActivityFeedOrderTotalsDTO): ActivityFeedOrderTotals | null => {
  if (totalValue === null || totalItems === null || totalPieces === null) {
    return null;
  }

  return { totalValue, totalItems, totalPieces };
};

const getOrderCreatedStatus = (newStatus?: string | null) => {
  if (!newStatus) {
    return null;
  }

  return findActivityFeedStatusChip(ORDER_CHIP_BY_STATUS, newStatus);
};

const resolveOrderCreatedPhraseKey = ({
  hasStatus,
  refs,
  selfOrderId,
}: OrderCreatedPhraseKeyParams) => {
  if (hasStatus) {
    return resolveOrderPhraseKey({
      key: LangEnum.ACTIVITY_FEED_ORDER_CREATED_WITH_STATUS,
      selfKey: LangEnum.ACTIVITY_FEED_ORDER_CREATED_SELF_WITH_STATUS,
      refs,
      selfOrderId,
    });
  }

  return resolveOrderPhraseKey({
    key: LangEnum.ACTIVITY_FEED_ORDER_CREATED,
    selfKey: LangEnum.ACTIVITY_FEED_ORDER_CREATED_SELF,
    refs,
    selfOrderId,
  });
};

const resolveOrderCreatedPhrase = (
  row: ActivityFeedRowOf<'order.created'>,
  selfOrderId?: string,
): ActivityFeedPhrase => {
  const status = getOrderCreatedStatus(row.payload.newStatus);

  return {
    key: resolveOrderCreatedPhraseKey({
      hasStatus: Boolean(status),
      refs: row.refs,
      selfOrderId,
    }),
    values: {
      ...getActorValues(row.actor),
      orderCode: row.payload.orderCode,
    },
    actorLabel: getActorLabel(row.actor, row.source),
    status,
    details: getOriginDetails(row.actor, row.source),
    totals: getOrderTotals(row.payload),
    attainment: null,
    lossReason: null,
    childOrders: null,
    refHref: getOrderRefHref(row.refs, selfOrderId),
  };
};

const resolveOrderStatusPhrase = (
  row: ActivityFeedRowOf<'order.status.updated'>,
  selfOrderId?: string,
): ActivityFeedPhrase | null => {
  const status = findActivityFeedStatusChip(ORDER_CHIP_BY_STATUS, row.payload.newStatus);

  if (!status) {
    return null;
  }

  return {
    key: resolveOrderPhraseKey({
      key: LangEnum.ACTIVITY_FEED_ORDER_STATUS_MOVED,
      selfKey: LangEnum.ACTIVITY_FEED_ORDER_STATUS_MOVED_SELF,
      refs: row.refs,
      selfOrderId,
    }),
    values: {
      ...getActorValues(row.actor),
      orderCode: row.payload.orderCode,
    },
    actorLabel: getActorLabel(row.actor, row.source),
    status,
    details: [...getReasonDetails(row.payload.reason), ...getOriginDetails(row.actor, row.source)],
    totals: getOrderTotals(row.payload),
    attainment: null,
    lossReason: null,
    childOrders: null,
    refHref: getOrderRefHref(row.refs, selfOrderId),
  };
};

const resolveOrderIntegrationStatusPhrase = (
  row: ActivityFeedRowOf<'order.integration_status.updated'>,
  selfOrderId?: string,
): ActivityFeedPhrase | null => {
  const { orderCode, integrationStatus, message } = row.payload;
  const status = findActivityFeedStatusChip(INTEGRATION_CHIP_BY_STATUS, integrationStatus);

  if (!status) {
    return null;
  }

  return {
    key: resolveOrderPhraseKey({
      key: LangEnum.ACTIVITY_FEED_ORDER_INTEGRATION_STATUS_MOVED,
      selfKey: LangEnum.ACTIVITY_FEED_ORDER_INTEGRATION_STATUS_MOVED_SELF,
      refs: row.refs,
      selfOrderId,
    }),
    values: { orderCode },
    actorLabel: null,
    status,
    details: message ? [{ text: message }] : [],
    totals: null,
    attainment: null,
    lossReason: null,
    childOrders: null,
    refHref: getOrderRefHref(row.refs, selfOrderId),
  };
};

const resolveChildOrders = ({
  childOrderCodes,
  refs,
  selfOrderId,
}: ChildOrdersParams): ActivityFeedChildOrder[] => {
  const idByCode = new Map((refs.childOrders ?? []).map(({ code, id }) => [code, id]));

  return childOrderCodes.map((code) => ({
    code,
    href: getOrderRefHref({ orderId: idByCode.get(code) }, selfOrderId),
  }));
};

const resolveOrderSplitPhrase = (
  row: ActivityFeedRowOf<'order.split'>,
  selfOrderId?: string,
): ActivityFeedPhrase => {
  const { orderCode, childOrderCodes } = row.payload;

  return {
    key: resolveOrderPhraseKey({
      key: LangEnum.ACTIVITY_FEED_ORDER_SPLIT,
      selfKey: LangEnum.ACTIVITY_FEED_ORDER_SPLIT_SELF,
      refs: row.refs,
      selfOrderId,
    }),
    values: {
      ...getActorValues(row.actor),
      orderCode,
      count: childOrderCodes.length,
    },
    actorLabel: getActorLabel(row.actor, row.source),
    status: null,
    details: [],
    totals: null,
    attainment: null,
    lossReason: null,
    childOrders: resolveChildOrders({ childOrderCodes, refs: row.refs, selfOrderId }),
    refHref: getOrderRefHref(row.refs, selfOrderId),
  };
};

const getTargetPeriodDetails = ({
  periodStartDate,
  periodEndDate,
}: TargetPeriodRange): ActivityFeedPhraseDetail[] => {
  if (!periodStartDate || !periodEndDate) {
    return [];
  }

  return [
    {
      key: LangEnum.TARGET_NAME_PERIOD_RANGE,
      values: {
        start: formatDate(periodStartDate, TARGET_DATE_FORMAT),
        end: formatDate(periodEndDate, TARGET_DATE_FORMAT),
      },
    },
  ];
};

const getPlannedAttainment = (
  plannedTotal: string | null,
  unit: TargetUnit,
): ActivityFeedAttainment | null => {
  if (plannedTotal === null) {
    return null;
  }

  return { attainedTotal: null, plannedTotal, conversion: null, unit };
};

const getTargetStatusAttainment = ({
  newStatus,
  attainedTotal,
  plannedTotal,
  conversion,
  unit,
}: TargetStatusAttainmentParams): ActivityFeedAttainment | null => {
  const valueDetail = isTargetStatus(newStatus) ? TARGET_VALUE_DETAIL_BY_STATUS[newStatus] : null;

  if (!valueDetail || plannedTotal === null) {
    return null;
  }
  if (valueDetail === TargetValueDetail.PLANNED || attainedTotal === null) {
    return getPlannedAttainment(plannedTotal, unit);
  }

  return { attainedTotal, plannedTotal, conversion, unit };
};

const getTargetLossReason = ({
  newStatus,
  lossReason,
}: TargetLossReasonParams): LangEnum | null => {
  if (newStatus !== TargetStatus.CLOSED_LOST || !lossReason) {
    return null;
  }

  return TARGET_LOSS_REASON_LABEL[lossReason] ?? null;
};

const resolveTargetCreatedPhrase = (
  row: ActivityFeedRowOf<'target.created'>,
  selfTargetId?: string,
): ActivityFeedPhrase | null => {
  const status = findActivityFeedStatusChip(TARGET_CHIP_BY_STATUS, row.payload.status);

  if (!status) {
    return null;
  }

  return {
    ...resolveTargetPhrase({
      ownFeedKey: LangEnum.ACTIVITY_FEED_TARGET_CREATED,
      withCodeKey: LangEnum.ACTIVITY_FEED_TARGET_CREATED_WITH_CODE,
      targetCode: row.payload.targetCode,
      refs: row.refs,
      selfTargetId,
      values: getActorValues(row.actor),
    }),
    actorLabel: getActorLabel(row.actor, row.source),
    status,
    details: [...getTargetPeriodDetails(row.payload), ...getOriginDetails(row.actor, row.source)],
    totals: null,
    attainment: getPlannedAttainment(row.payload.plannedTotal, row.payload.unit),
    lossReason: null,
    childOrders: null,
    refHref: getTargetRefHref(row.refs, selfTargetId),
  };
};

const resolveTargetMilestonePhrase = (
  row: ActivityFeedRowOf<'target.attainment.milestone_reached'>,
  selfTargetId?: string,
): ActivityFeedPhrase => {
  return {
    ...resolveTargetPhrase({
      ownFeedKey: LangEnum.ACTIVITY_FEED_TARGET_MILESTONE_REACHED,
      withCodeKey: LangEnum.ACTIVITY_FEED_TARGET_MILESTONE_REACHED_WITH_CODE,
      targetCode: row.payload.targetCode,
      refs: row.refs,
      selfTargetId,
      values: { milestone: row.payload.milestone },
    }),
    actorLabel: null,
    status: null,
    details: [],
    totals: null,
    attainment: {
      attainedTotal: row.payload.attainedTotal,
      plannedTotal: row.payload.plannedTotal,
      conversion: null,
      unit: row.payload.unit,
    },
    lossReason: null,
    childOrders: null,
    refHref: getTargetRefHref(row.refs, selfTargetId),
  };
};

const resolveTargetStatusPhrase = (
  row: ActivityFeedRowOf<'target.status.updated'>,
  selfTargetId?: string,
): ActivityFeedPhrase | null => {
  const status = findActivityFeedStatusChip(TARGET_CHIP_BY_STATUS, row.payload.newStatus);

  if (!status) {
    return null;
  }

  return {
    ...resolveTargetPhrase({
      ownFeedKey: LangEnum.ACTIVITY_FEED_TARGET_STATUS_MOVED,
      withCodeKey: LangEnum.ACTIVITY_FEED_TARGET_STATUS_MOVED_WITH_CODE,
      targetCode: row.payload.targetCode,
      refs: row.refs,
      selfTargetId,
      values: getActorValues(row.actor),
    }),
    actorLabel: getActorLabel(row.actor, row.source),
    status,
    details: getOriginDetails(row.actor, row.source),
    totals: null,
    attainment: getTargetStatusAttainment(row.payload),
    lossReason: getTargetLossReason(row.payload),
    childOrders: null,
    refHref: getTargetRefHref(row.refs, selfTargetId),
  };
};

const noPhraseFor = (_row: ActivityFeedRowDTO) => null;

export const resolveActivityFeedPhrase = (
  row: ActivityFeedRowDTO,
  self?: ActivityFeedSelfRefs,
): ActivityFeedPhrase | null => {
  switch (row.eventType) {
    case 'customer.created':
      return resolveCustomerCreatedPhrase(row);
    case 'customer.status.updated':
      return resolveCustomerStatusPhrase(row);
    case 'customer.integration_status.updated':
      return resolveCustomerIntegrationStatusPhrase(row);
    case 'invite.created':
      return resolveConnectionCreatedPhrase(row);
    case 'invite.status.updated':
      return resolveConnectionStatusPhrase(row);
    case 'order.created':
      return resolveOrderCreatedPhrase(row, self?.orderId);
    case 'order.status.updated':
      return resolveOrderStatusPhrase(row, self?.orderId);
    case 'order.integration_status.updated':
      return resolveOrderIntegrationStatusPhrase(row, self?.orderId);
    case 'order.split':
      return resolveOrderSplitPhrase(row, self?.orderId);
    case 'invite.removed':
      return resolveConnectionRemovedPhrase(row);
    case 'target.attainment.milestone_reached':
      return resolveTargetMilestonePhrase(row, self?.targetId);
    case 'target.created':
      return resolveTargetCreatedPhrase(row, self?.targetId);
    case 'target.status.updated':
      return resolveTargetStatusPhrase(row, self?.targetId);
    default:
      return noPhraseFor(row);
  }
};
